import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { changeLocation } from 'ha/modules/URLLocationHandler';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { ListingCardSkeleton } from 'ha/pages/Search/components/ListingCardSkeleton';
import { Grid } from '@hbf/dsl';

import { loadSimilarListings } from '../actions/loadSimilarListings';
import { ListingSimilarListings } from '../components/ListingSimilarListings';

import {
  getCity,
  getCityLocalized,
  getSimilarListings,
  getPathToSearch,
  getListingEntity,
  getIsImperialSystem,
  getCountryCode,
  getLoadingSimilarListings,
} from '../selectors';
import { SIMILAR_LISTING_CONFIG } from '../constants';

const useStyles = makeStyles()(theme => ({
  skeletonContainer: {
    padding: theme.spacing(6, 0),
  },
}));

const SimilarListings: React.FC = () => {
  const dispatch = useDispatch();
  const { ldpHideSimilarListings, useSimilarListingAPI } = useFeatureFlags();
  const countryCode = useSelector(getCountryCode);
  const { classes } = useStyles();

  const useSimilarListingsAPIFlag =
    useSimilarListingAPI === 'on' &&
    SIMILAR_LISTING_CONFIG.countryCodes.includes(countryCode.toLowerCase());

  const onClick = React.useCallback(
    nextUrl => {
      dispatch(changeLocation(nextUrl));
    },
    [dispatch],
  );

  const listing = useSelector(getListingEntity);

  React.useEffect(() => {
    if (ldpHideSimilarListings === 'on') {
      return;
    }

    dispatch(loadSimilarListings(listing, useSimilarListingsAPIFlag));
  }, [dispatch, listing, ldpHideSimilarListings, useSimilarListingsAPIFlag]);

  const city = useSelector(getCity);
  const cityLocalized = useSelector(getCityLocalized);
  const similarListings = useSelector(getSimilarListings);
  const pathToSearch = useSelector(getPathToSearch);
  const isImperialSystem = useSelector(getIsImperialSystem);
  const isLoadingSimilarListings = useSelector(getLoadingSimilarListings);

  if (isLoadingSimilarListings) {
    return (
      <Grid
        container
        justifyContent="center"
        direction="column"
        className={classes.skeletonContainer}
      >
        <Grid container item spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <ListingCardSkeleton />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <ListingCardSkeleton />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <ListingCardSkeleton />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <ListingCardSkeleton />
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (!similarListings.length) return null;

  return (
    <ListingSimilarListings
      city={city}
      cityLocalized={cityLocalized}
      listings={similarListings}
      pathToSearch={pathToSearch}
      onClick={onClick}
      isImperialSystem={isImperialSystem}
    />
  );
};

export { SimilarListings };
